
import { Brain, Heart, Leaf, Sparkles, Star, Zap, Target, Users, Award, BookOpen } from "lucide-react";
import { usePageContent } from "@/hooks/usePageContent";

const ICON_MAP: Record<string, React.ElementType> = {
  Brain, Heart, Leaf, Sparkles, Star, Zap, Target, Users, Award, BookOpen,
};

const DEFAULT_DIFFERENTIALS = [
  { icon: 'Brain',    number: '01', title: 'Entende o porquê da sua alimentação', description: 'Olhamos além do prato. Entendemos seus gatilhos emocionais, sua rotina e sua relação com a comida para criar uma estratégia que funcione de verdade.' },
  { icon: 'Heart',    number: '02', title: 'Sem dietas radicais ou julgamentos',  description: 'Cada paciente tem sua história. Respeitamos seu ritmo e criamos um plano que cabe na sua vida, sem culpa e sem proibições desnecessárias.' },
  { icon: 'Leaf',     number: '03', title: 'Ciência aplicada ao dia a dia',       description: 'Cada orientação tem base científica e é adaptada para ser prática. Você aprende a se alimentar bem de forma sustentável, não apenas durante o tratamento.' },
  { icon: 'Sparkles', number: '04', title: 'Acompanhamento real e contínuo',      description: 'Não desaparecemos depois da consulta. O suporte é contínuo para garantir que você mantenha o progresso e supere os momentos difíceis.' },
];

export function Approaches() {
  const { content, getText } = usePageContent('home');

  const headerLabel    = getText('differential', 'header_label',    'Por que funciona');
  const headerTitle    = getText('differential', 'header_title',    'Uma abordagem que respeita você');
  const headerSubtitle = getText('differential', 'header_subtitle', 'A diferença entre mais uma dieta fracassada e uma transformação real está na abordagem.');

  const dbItems = content
    .filter(c => c.section === 'differential' && c.content_key.startsWith('item_'))
    .sort((a, b) => a.display_order - b.display_order);

  const differentials = dbItems.length > 0
    ? dbItems.map((item, i) => ({
        icon: (item.content_value.icon as string) ?? 'Brain',
        number: String(i + 1).padStart(2, '0'),
        title: (item.content_value.title as string) ?? '',
        description: (item.content_value.description as string) ?? '',
      }))
    : DEFAULT_DIFFERENTIALS;

  // Split headerTitle into main + highlighted last word
  const titleWords = headerTitle.trim().split(' ');
  const titleHighlight = titleWords.pop();
  const titleMain = titleWords.join(' ');

  return (
    <section className="py-28 bg-brand-primary relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-white/[0.03]" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-brand-softYellow/5" />
      <div className="absolute top-1/2 right-0 w-px h-3/4 -translate-y-1/2 bg-gradient-to-b from-transparent via-white/10 to-transparent" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
          <div className="max-w-xl">
            <p className="text-brand-softYellow font-bold text-xs uppercase tracking-[0.2em] font-primary mb-4">
              {headerLabel}
            </p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white font-primary leading-tight">
              {titleMain}{' '}
              <br className="hidden md:block" />
              <span className="title-special text-brand-softYellow text-5xl md:text-6xl">{titleHighlight}</span>
            </h2>
          </div>
          <p className="text-white/50 text-base font-primary font-light leading-relaxed max-w-sm md:text-right">
            {headerSubtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {differentials.map((item, i) => {
            const Icon = ICON_MAP[item.icon] ?? Brain;
            return (
              <div key={i} className="group relative bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 hover:border-brand-softYellow/30 rounded-3xl p-8 transition-all duration-400 overflow-hidden">
                <span className="absolute -top-3 -right-1 text-[7rem] font-extrabold font-primary text-white/[0.04] leading-none select-none">
                  {item.number}
                </span>
                <div className="relative flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-brand-softYellow/10 border border-brand-softYellow/20 flex items-center justify-center group-hover:bg-brand-softYellow/20 transition-colors">
                    <Icon className="w-5 h-5 text-brand-softYellow" />
                  </div>
                  <div className="pt-1">
                    <span className="text-brand-softYellow/50 text-xs font-bold font-primary tracking-widest mb-2 block">{item.number}</span>
                    <h3 className="text-white font-bold font-primary text-lg mb-2.5 leading-snug group-hover:text-brand-softYellow transition-colors duration-300">{item.title}</h3>
                    <p className="text-white/55 font-primary font-light text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
                <div className="absolute bottom-0 left-8 right-8 h-px bg-brand-softYellow/0 group-hover:bg-brand-softYellow/30 transition-all duration-300" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
